import React from "react";
import useThemeColor from "../../../../hooks/useThemeColor";

/**
 * Site-standard primary action button.
 * Matches Manage Deals "Add Deal" header button:
 * theme gradient, 8px radius, 14px/600, soft shadow, optional icon.
 */
const ThemeButton = ({
  children,
  title,
  onClick,
  type = "button",
  disabled = false,
  icon = null,
  iconClassName = "",
  variant = "primary",
  className = "",
  style: styleOverride = {},
  ...rest
}) => {
  const themeColor = useThemeColor();
  const label = children ?? title;

  const baseClass = "btn px-4";
  const baseStyle = {
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: 600,
    transition: "all 0.3s ease",
    opacity: disabled ? 0.65 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  };

  const variants = {
    primary: {
      className: baseClass,
      style: {
        ...baseStyle,
        backgroundColor: themeColor,
        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
        border: "none",
        color: "#fff",
        boxShadow: `0 2px 8px ${themeColor}40`,
      },
      onMouseEnter: (e) => {
        if (disabled) return;
        e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}60`;
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}40`;
      },
    },
    secondary: {
      className: baseClass,
      style: {
        ...baseStyle,
        background: "#6c757d",
        border: "none",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(108, 117, 125, 0.35)",
      },
      onMouseEnter: (e) => {
        if (disabled) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(108, 117, 125, 0.45)";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(108, 117, 125, 0.35)";
      },
    },
    ghost: {
      className: baseClass,
      style: {
        ...baseStyle,
        background: "transparent",
        border: `1px solid ${themeColor}`,
        color: themeColor,
        boxShadow: "none",
      },
      onMouseEnter: (e) => {
        if (disabled) return;
        e.currentTarget.style.background = `${themeColor}18`;
        e.currentTarget.style.transform = "translateY(-1px)";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.transform = "translateY(0)";
      },
    },
    danger: {
      className: baseClass,
      style: {
        ...baseStyle,
        background: "transparent",
        border: "1px solid #dc3545",
        color: "#dc3545",
        boxShadow: "none",
      },
      onMouseEnter: (e) => {
        if (disabled) return;
        e.currentTarget.style.background = "rgba(220, 53, 69, 0.12)";
        e.currentTarget.style.transform = "translateY(-1px)";
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.transform = "translateY(0)";
      },
    },
  };

  const config = variants[variant] || variants.primary;
  const iconTone =
    iconClassName ||
    (variant === "primary" || variant === "secondary" ? "text-white" : "");

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${config.className} ${className}`.trim()}
      style={{ ...config.style, ...styleOverride }}
      onMouseEnter={config.onMouseEnter}
      onMouseLeave={config.onMouseLeave}
      {...rest}
    >
      {icon ? <i className={`${icon} fa-lg me-2 ms-n2 ${iconTone}`} /> : null}
      {label}
    </button>
  );
};

export default ThemeButton;
